import { isShareMode } from '../../lib/shareMode';
import { useNavigationStore } from '../../store/navigationStore';
import type { WorkspaceResourceType } from './workspaceApi';
import { endpointType } from './workspaceEndpoints';
import { resourceInProject, useWorkspaceStore } from './workspaceStore';

function isModuleInventory(type: WorkspaceResourceType, page: string) {
  if (page === 'files') return true;
  const pages: Record<WorkspaceResourceType, string[]> = {
    app: ['apps', 'apps-app'], pipeline: ['pipelines', 'ontology', 'graph'],
    function: ['logic'], agent: ['agents'], object_type: ['ontology', 'graph', 'data'],
    notebook: ['workbench'], connector: ['connectors', 'ontology', 'graph'],
  };
  return pages[type].includes(page) || (type === 'app' && page.startsWith('app-'));
}

/** Restrict module inventories to their project; never replace authentication. */
export function makeWorkspaceFetch(transport: typeof fetch): typeof fetch {
  return async (input, init) => {
    if (isShareMode()) return transport(input, init);
    const url = input instanceof Request ? input.url : String(input);
    const type = endpointType(url);
    if (!type) return transport(input, init);
    const headers = new Headers(input instanceof Request ? input.headers : undefined);
    new Headers(init?.headers).forEach((value, key) => headers.set(key, value));
    const inventory = headers.get('x-workspace-inventory') === 'all';
    headers.delete('x-workspace-inventory');
    const method = (init?.method || (input instanceof Request ? input.method : 'GET')).toUpperCase();
    const startedAsInventory = isModuleInventory(type, useNavigationStore.getState().currentPage);
    const started = useWorkspaceStore.getState();
    const response = await transport(input, { ...init, headers });
    if (!response.ok) return response;
    const current = useWorkspaceStore.getState();
    // Never pass a previous user's response into a newly mounted workspace.
    if (started.identity !== current.identity) return new Response('[]', { status: 409, headers: { 'Content-Type': 'application/json' } });
    if (inventory) return response;
    // Reference pickers inside editors keep their authorized inventory. Only
    // the module's own resource list is narrowed to the current project.
    if (method === 'GET' && current.projectId && (startedAsInventory || isModuleInventory(type, useNavigationStore.getState().currentPage))) {
      const data = await response.clone().json();
      if (!Array.isArray(data)) return response;
      const filtered = data.filter(row => resourceInProject(type, String(row.id)));
      const responseHeaders = new Headers(response.headers);
      responseHeaders.delete('content-length');
      responseHeaders.delete('content-encoding');
      return new Response(JSON.stringify(filtered), { status: response.status, headers: responseHeaders });
    }
    if (method === 'POST' && started.projectId) {
      const resource = await response.clone().json();
      if (resource?.id) {
        try {
          await useWorkspaceStore.getState().place(type, String(resource.id), started.projectId);
        } catch {
          useWorkspaceStore.setState({ error: 'El recurso se creó, pero no se pudo agregar al proyecto. Puedes organizarlo desde Archivos → Todos los proyectos.' });
        }
      }
    }
    return response;
  };
}
